import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Hourglass, CheckCircle, XCircle, Phone, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import BulkApproveButton from "./BulkApproveButton";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const PaymentConfirmationsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/payment-confirmations`);
      setItems(data);
    } catch {
      toast.error("فشل تحميل التأكيدات");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAction = async (id, action) => {
    const msg = action === "approve"
      ? "تأكيد الاستلام؟ سيتم خصم المبلغ المتبقي من حساب الزبون كدين."
      : "رفض التأكيد؟ سيُعاد الطلب للسائق ليعيد المراجعة.";
    if (!(await confirmDialog(msg))) return;
    setBusy(id);
    try {
      await axios.patch(`${API}/admin/payment-confirmations/${id}`, { action });
      toast.success(action === "approve" ? "تم تأكيد الاستلام وتسديد الطلب" : "تم رفض التأكيد");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="pc-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
          <Hourglass className="w-6 h-6 text-[#F5A623]" />
          تأكيدات استلام المبلغ
          {items.length > 0 && (
            <span className="bg-[#F5A623] text-white text-xs px-2.5 py-0.5 rounded-full font-bold animate-pulse">
              {items.length} بانتظار
            </span>
          )}
        </h2>
        <BulkApproveButton resource="payment-confirmations" label="موافقة على الكل" count={items.length} onDone={fetchData} />
      </div>

      {loading ? (
        <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E4EBE6]">
          <Hourglass className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
          <p className="text-[#5C6E64]">لا توجد تأكيدات بانتظار المراجعة</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((o, idx) => {
            const paid = Number(o.submitted_amount_paid || 0);
            const remaining = Number(o.submitted_amount_remaining || 0);
            return (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                data-testid={`pc-${o.id}`}
                className="bg-white border-2 border-[#F5A623]/30 rounded-2xl p-4 space-y-3"
              >
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#111814]">{o.user_name}</p>
                    {o.customer_id && (
                      <span className="text-xs bg-[#E0F2FE] px-2 py-0.5 rounded-full font-mono font-bold text-[#2D4A3E] inline-block mt-1">
                        ايباد: {o.customer_id}
                      </span>
                    )}
                    <a href={`tel:${o.user_phone}`} className="text-sm text-[#E85D45] flex items-center gap-1 mt-1 hover:underline">
                      <Phone className="w-3 h-3" />{o.user_phone}
                    </a>
                  </div>
                  <span className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 bg-[#F5A623]/20 text-[#F5A623]">
                    <Hourglass className="w-3 h-3" />
                    بانتظار التأكيد
                  </span>
                </div>

                {o.driver_name && (
                  <div className="bg-[#E0F2FE] rounded-xl px-3 py-2 text-sm flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[#4A90E2]" />
                    <span>السائق: <span className="font-bold">{o.driver_name}</span></span>
                    {o.driver_phone && <a href={`tel:${o.driver_phone}`} className="mr-auto text-[#E85D45] text-xs hover:underline">{o.driver_phone}</a>}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#F3F6F4] rounded-xl p-2.5 text-center">
                    <p className="text-[10px] text-[#5C6E64]">إجمالي الطلب</p>
                    <p className="font-bold text-sm text-[#111814]">{fmt(o.total)} د.ع</p>
                  </div>
                  <div className="bg-[#E8F5E9] rounded-xl p-2.5 text-center">
                    <p className="text-[10px] text-[#5C6E64]">المبلغ المستلم</p>
                    <p className="font-bold text-sm text-[#7ED321]">{fmt(paid)} د.ع</p>
                  </div>
                  <div className={`rounded-xl p-2.5 text-center ${remaining > 0 ? "bg-[#FFE8E5]" : "bg-[#F3F6F4]"}`}>
                    <p className="text-[10px] text-[#5C6E64]">الدين المتبقي</p>
                    <p className={`font-bold text-sm ${remaining > 0 ? "text-[#E85D45]" : "text-[#7ED321]"}`}>
                      {fmt(remaining)} د.ع
                    </p>
                  </div>
                </div>

                {remaining > 0 && (
                  <div className="bg-[#FFF8E1] border border-[#F5A623]/40 rounded-xl px-3 py-2 text-xs flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#F5A623]" />
                    عند الموافقة، سيُضاف {fmt(remaining)} د.ع كدين على حساب الزبون
                  </div>
                )}

                {o.delivery_note && (
                  <div className="bg-[#FFF8E1] border border-[#F5A623]/30 rounded-xl px-3 py-2 text-sm italic">
                    ملاحظة السائق: {o.delivery_note}
                  </div>
                )}

                <p className="text-xs text-[#5C6E64]">أُرسل: {formatDate(o.payment_submitted_at)}</p>

                <div className="flex gap-2">
                  <button onClick={() => handleAction(o.id, "approve")} disabled={busy === o.id}
                    data-testid={`pc-approve-${o.id}`}
                    className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 rounded-full text-sm disabled:opacity-50 flex items-center justify-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    تأكيد وتسديد
                  </button>
                  <button onClick={() => handleAction(o.id, "reject")} disabled={busy === o.id}
                    data-testid={`pc-reject-${o.id}`}
                    className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm disabled:opacity-50 flex items-center justify-center gap-1">
                    <XCircle className="w-4 h-4" />
                    رفض
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PaymentConfirmationsTab;
